import { useCallback, useEffect, useRef, useState } from 'react';

import { routeMatches } from './targets';

/**
 * THE STEP RUNNER (KPR-611). Which step is showing, how Next, Back, skipping
 * and ending move it, and what the callbacks are told. No DOM, no layout:
 * `TourRunner` draws it and watches the page for the person's action.
 *
 * Steps are met lazily. A step is skipped when its condition is false or
 * throws, when it is a navigate step for a route the person is already on, or
 * (the runner is told by `skipCurrent`) when its target never turned up. A
 * skip is logged to the console in development and never otherwise heard of.
 * Back walks the steps that were actually shown, not the definition's list.
 *
 * It ends exactly once, with one of three outcomes:
 *   completed  the person finished it (Finish, or ran past the last step)
 *   dismissed  the person left it (Exit tour, Esc)
 *   stopped    the app ended it (stop(), a replacing tour, unmount, nothing
 *              to show). Not the person's choice, so it is never a dismissal.
 *
 * The callbacks get one object: { tourId, version, outcome, reason, stepId,
 * stepIndex, position, total }. `onComplete` / `onDismiss` fire for their
 * outcome, then `onExit` for every end. The runner keeps nothing anywhere.
 */

const devLog = (message) => {
	if (process.env.NODE_ENV === 'development') console.info(`[tour] ${message}`);
};

const conditionPasses = (tour, step, context, { log = false } = {}) => {
	if (!step.condition) return true;
	try {
		return Boolean(step.condition(context));
	} catch (error) {
		if (log) devLog(`"${tour.id}" step "${step.id}" skipped: its condition threw (${error && error.message})`);
		return false;
	}
};

// One step visit: the same step reached again by Back is a new visit.
const keyOf = (current) => `${current.index}:${current.stack.length}:${current.dir}`;

export default function useTourRunner({ tour, context, pathname, onExit, onComplete, onDismiss, onStep, onEnded }) {
	const steps = tour.steps;
	const callbacks = useRef({});
	callbacks.current = { onExit, onComplete, onDismiss, onStep, onEnded };
	const world = useRef({});
	world.current = { ...(context || {}), pathname };

	const stateRef = useRef(null);
	const [state, setState] = useState(null);
	const endedRef = useRef(false);
	const startedRef = useRef(false);
	const aliveRef = useRef(false);
	const [ended, setEnded] = useState(null);

	const commit = useCallback((next) => {
		stateRef.current = next;
		setState(next);
	}, []);

	const summary = useCallback((current) => {
		const index = current && current.index !== null ? current.index : null;
		const visible = current ? steps.map((_, i) => i).filter((i) => !current.skipped.includes(steps[i].id)
			&& conditionPasses(tour, steps[i], world.current)) : [];
		const found = index === null ? -1 : visible.indexOf(index);
		return {
			stepId: index === null ? null : steps[index].id,
			stepIndex: index,
			position: found >= 0 ? found + 1 : (current ? current.stack.length + 1 : 0),
			total: visible.length,
		};
	}, [steps, tour]);

	const finish = useCallback((outcome, reason) => {
		if (endedRef.current) return;
		endedRef.current = true;
		const payload = { tourId: tour.id, version: tour.version, outcome, reason, ...summary(stateRef.current) };
		setEnded(payload);
		const call = (fn) => {
			if (typeof fn !== 'function') return;
			try {
				fn(payload);
			} catch (error) {
				console.error(error);
			}
		};
		if (outcome === 'completed') call(callbacks.current.onComplete);
		if (outcome === 'dismissed') call(callbacks.current.onDismiss);
		call(callbacks.current.onExit);
		// The component's own housekeeping (focus goes back) comes last.
		call(callbacks.current.onEnded);
	}, [summary, tour]);

	// The first eligible step at or after `start`, walking `dir`.
	const findFrom = useCallback((start, dir) => {
		const skipped = [];
		for (let i = start; i >= 0 && i < steps.length; i += dir) {
			const step = steps[i];
			if (!conditionPasses(tour, step, world.current, { log: true })) {
				devLog(`"${tour.id}" step "${step.id}" skipped: its condition is false`);
				continue;
			}
			if (dir === 1 && step.kind === 'navigate' && routeMatches(step.route, world.current.pathname)) {
				devLog(`"${tour.id}" step "${step.id}" skipped: already on ${step.route}`);
				skipped.push(step.id);
				continue;
			}
			return { index: i, skipped };
		}
		return { index: -1, skipped };
	}, [steps, tour]);

	// Start. Guarded, so React's development double-run of effects starts once.
	useEffect(() => {
		if (startedRef.current) return;
		startedRef.current = true;
		const first = findFrom(0, 1);
		if (first.index < 0) {
			finish('stopped', 'no-applicable-steps');
			return;
		}
		commit({ index: first.index, stack: [], dir: 1, from: null, skipped: first.skipped });
	}, [commit, findFrom, finish]);

	// Unmounting ends the tour as stopped. React may unmount and remount an
	// effect straight away in development, so the end waits a tick to see
	// whether the runner came back.
	useEffect(() => {
		aliveRef.current = true;
		return () => {
			aliveRef.current = false;
			setTimeout(() => {
				if (!aliveRef.current) finish('stopped', 'unmount');
			}, 0);
		};
	}, [finish]);

	// `expectedKey` guards a deferred call (a click that advances a tick
	// later): it does nothing if the step it was meant for is gone.
	const next = useCallback((expectedKey) => {
		const current = stateRef.current;
		if (!current || endedRef.current) return;
		if (expectedKey !== undefined && keyOf(current) !== expectedKey) return;
		const found = findFrom(current.index + 1, 1);
		const skipped = [...current.skipped, ...found.skipped];
		if (found.index < 0) {
			stateRef.current = { ...current, skipped };
			finish('completed', 'finished');
			return;
		}
		commit({ index: found.index, stack: [...current.stack, current.index], dir: 1, from: null, skipped });
	}, [commit, findFrom, finish]);

	const back = useCallback(() => {
		const current = stateRef.current;
		if (!current || endedRef.current || current.stack.length === 0) return;
		const stack = current.stack.slice(0, -1);
		commit({ ...current, index: current.stack[current.stack.length - 1], stack, dir: -1, from: current.index });
	}, [commit]);

	// Leave the step on screen without the person having done it: its target
	// never turned up or went away for good ('missing'), or the field it waits
	// for already holds a value ('satisfied').
	const skipCurrent = useCallback((reason, expectedKey) => {
		const current = stateRef.current;
		if (!current || endedRef.current) return;
		if (expectedKey !== undefined && keyOf(current) !== expectedKey) return;
		const step = steps[current.index];
		devLog(reason === 'satisfied'
			? `"${tour.id}" step "${step.id}" skipped: data-tour="${step.target}" already holds a value`
			: `"${tour.id}" step "${step.id}" skipped: no element with data-tour="${step.target}"`);
		const skipped = [...current.skipped, step.id];
		if (current.dir === -1) {
			if (current.stack.length > 0) {
				commit({ ...current, index: current.stack[current.stack.length - 1], stack: current.stack.slice(0, -1), skipped });
				return;
			}
			// Nothing earlier can be shown: go back to where Back was pressed.
			commit({ index: current.from, stack: [], dir: 1, from: null, skipped });
			return;
		}
		const found = findFrom(current.index + 1, 1);
		const all = [...skipped, ...found.skipped];
		if (found.index < 0) {
			stateRef.current = { ...current, skipped: all };
			if (current.stack.length > 0) finish('completed', 'finished');
			else finish('stopped', 'no-applicable-steps');
			return;
		}
		commit({ index: found.index, stack: current.stack, dir: 1, from: null, skipped: all });
	}, [commit, findFrom, finish, steps, tour]);

	const dismiss = useCallback((reason = 'exit') => finish('dismissed', reason), [finish]);
	const stop = useCallback((reason = 'stopped') => finish('stopped', reason), [finish]);

	// Tell the caller each step as it is reached.
	const reached = state && state.index !== null ? state.index : null;
	useEffect(() => {
		if (reached === null || endedRef.current) return;
		const fn = callbacks.current.onStep;
		if (typeof fn !== 'function') return;
		try {
			fn({ tourId: tour.id, version: tour.version, ...summary(stateRef.current) });
		} catch (error) {
			console.error(error);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [reached, tour]);

	const live = !ended && state && state.index !== null ? state : null;
	const view = live ? summary(live) : { stepId: null, stepIndex: null, position: 0, total: 0 };
	return {
		step: live ? steps[live.index] : null,
		stepKey: live ? keyOf(live) : null,
		direction: live ? live.dir : 1,
		position: view.position,
		total: view.total,
		canBack: Boolean(live && live.stack.length > 0),
		isLast: Boolean(live && lastReachableIndex(steps, live, world.current, tour) === live.index),
		ended,
		next,
		back,
		skipCurrent,
		dismiss,
		stop,
	};
}

// Is there no step after this one that could still be shown? Conditions only;
// a target that turns out missing later shortens the tour without changing
// the button until it happens.
function lastReachableIndex(steps, current, context, tour) {
	for (let i = steps.length - 1; i > current.index; i -= 1) {
		if (current.skipped.includes(steps[i].id)) continue;
		if (conditionPasses(tour, steps[i], context)
			&& !(steps[i].kind === 'navigate' && routeMatches(steps[i].route, context.pathname))) return i;
	}
	return current.index;
}
